import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { CalendarEvent, CalendarLocale, CalendarNavigateEvent, CalendarView } from 'ng-full-calendar';
import { StaffAcademicCalendarService, StaffAcademicEvent } from '../services/staff-academic-calendar.service';
import { StaffSessionService } from '../services/staff-session.service';

// Read-only staff Academic Calendar, reusing the same ngfc-calendar UI the
// web-admin's academic-calendar.component.ts uses, but wired to the staff-
// specific getEventInfo endpoint (per-month event list, no categories/
// holiday/CRUD — see StaffAcademicCalendarService). [editable]="false" keeps
// ngfc-calendar's built-in create/edit modal from ever opening; a plain
// info popup below replaces it for viewing an event's title/description.
@Component({
  selector: 'app-staff-academic-calendar',
  templateUrl: './staff-academic-calendar.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-academic-calendar.component.css']
})
export class StaffAcademicCalendarComponent implements OnInit {
  staffId: number | string | null = null;

  events: CalendarEvent[] = [];
  ngfcDate: Date = new Date();
  ngfcView: CalendarView = 'month';
  ngfcLocale: CalendarLocale | null = null;

  loadingData = true;
  private currentRangeKey = '';

  eventPopupVisible = false;
  selectedEvent: CalendarEvent | null = null;

  constructor(
    private staffAcademicCalendarService: StaffAcademicCalendarService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const staffDetails = this.staffSession.getStaffDetails();
    const info = this.staffSession.getSchoolAndStaffInfo();
    this.staffId = staffDetails?.staffId ?? info?.StaffId ?? null;

    this.setTranslatedCalendarLocale();
    this.loadEventsForVisibleMonth();
  }

  private setTranslatedCalendarLocale() {
    this.translate.get([
      'calendar.months',
      'calendar.monthsShort',
      'calendar.weekdays',
      'calendar.weekdaysShort'
    ]).subscribe(translated => {
      this.ngfcLocale = {
        weekdayNamesShort: translated['calendar.weekdaysShort'],
        weekdayNamesLong: translated['calendar.weekdays'],
        monthNamesShort: translated['calendar.monthsShort'],
        monthNamesLong: translated['calendar.months'],
        timeFormat: '12h',
      };
    });
  }

  // ngfc-calendar's own prev/next/today buttons (and any programmatic date
  // change) fire this — re-fetch only when the visible month actually
  // changed, not on every keystroke of internal state.
  onNavigate(navEvent: CalendarNavigateEvent) {
    this.ngfcDate = navEvent.date;
    this.loadEventsForVisibleMonth();
  }

  private async loadEventsForVisibleMonth() {
    if (!this.staffId) { this.loadingData = false; return; }
    const startDate = moment(this.ngfcDate).startOf('month').format('YYYY-MM-DD');
    const endDate = moment(this.ngfcDate).endOf('month').format('YYYY-MM-DD');
    const rangeKey = `${startDate}_${endDate}`;
    if (rangeKey === this.currentRangeKey) { return; }
    this.currentRangeKey = rangeKey;

    this.loadingData = true;
    try {
      const response = await firstValueFrom(this.staffAcademicCalendarService.getEventInfo(this.staffId, startDate, endDate));
      const eventList = response?.status ? (response.body?.eventList || []) : [];
      this.events = this.convertToCalendarEvents(eventList);
    } catch {
      this.events = [];
    } finally {
      this.loadingData = false;
    }
  }

  // Parses the API's ISO datetime as literal wall-clock numbers rather than
  // a real UTC instant, so ngfc-calendar (which reads Date via local-time
  // getters) doesn't shift the displayed time by the browser's timezone
  // offset — same approach as the web-admin calendar's parseApiDate.
  private parseApiDate(iso: string): Date {
    const [datePart, timePart] = iso.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = (timePart || '00:00').split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute);
  }

  private isFullDayRange(start: Date, end: Date): boolean {
    const startsAtMidnight = start.getHours() === 0 && start.getMinutes() === 0;
    const endsAtMidnight = end.getHours() === 0 && end.getMinutes() === 0;
    const endsAtDayClose = end.getHours() === 23 && end.getMinutes() === 59;
    return startsAtMidnight && (endsAtMidnight || endsAtDayClose);
  }

  private convertToCalendarEvents(eventList: StaffAcademicEvent[]): CalendarEvent[] {
    return eventList.map((event, index) => {
      const start = this.parseApiDate(event.startDate);
      const end = event.endDate ? this.parseApiDate(event.endDate) : start;
      return {
        id: `${index}-${event.eventName}-${event.startDate}`,
        title: event.eventName,
        start,
        end,
        allDay: this.isFullDayRange(start, end),
        color: event.colorCode,
        description: event.description,
      };
    });
  }

  handleEventClick(event: CalendarEvent) {
    this.selectedEvent = event;
    this.eventPopupVisible = true;
  }

  closeEventPopup() {
    this.eventPopupVisible = false;
    this.selectedEvent = null;
  }
}
